// src/components/chat/ChatInbox.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import pb from '../../lib/pb';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, injectStyles } from '../../shared/PostCard';
import {
  NC,
  decryptWithRing,
  isUserOnline,
  playSound,
  resolveRing,
  toTime,
} from './ChatConversation';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

/* ------------------------------------------------------------------ */
/* Styles (shared chat classes live in ChatConversation.js)             */
/* ------------------------------------------------------------------ */
const INBOX_CSS = `
.mfc-inbox{display:flex;flex-direction:column;height:100%;min-height:0;background:var(--mf-surface);color:var(--mf-text)}
.mfc-side-head{flex:none;display:flex;align-items:center;gap:10px;padding:16px 14px 6px 18px}
.mfc-side-title{margin:0;flex:1;display:flex;align-items:center;gap:10px;font-size:24px;font-weight:800;letter-spacing:-.02em}
.mfc-total{font-size:12px;font-weight:800;padding:2px 9px;border-radius:999px;background:var(--mf-accent-soft);color:var(--mf-accent)}

.mfc-search{position:relative;flex:none;margin:8px 14px 10px}
.mfc-search svg{position:absolute;left:14px;top:50%;transform:translateY(-50%);font-size:20px;color:var(--mf-faint);pointer-events:none}
.mfc-search input{width:100%;height:42px;border-radius:14px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);color:var(--mf-text);
  padding:0 14px 0 42px;font:inherit;font-size:16px;outline:none;transition:border-color .15s,box-shadow .15s}
.mfc-search input::placeholder{color:var(--mf-faint)}
.mfc-search input:focus{border-color:var(--mf-accent);box-shadow:0 0 0 3px var(--mf-accent-soft)}

.mfc-in-list{flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;list-style:none;margin:0;padding:0 8px 14px;-webkit-overflow-scrolling:touch}
.mfc-friend{width:100%;display:flex;align-items:center;gap:12px;padding:10px;border:0;border-radius:16px;background:transparent;color:inherit;text-align:left;
  cursor:pointer;font:inherit;transition:background-color .15s}
.mfc-friend:hover{background:var(--mf-surface-2)}
.mfc-friend.is-active{background:var(--mf-accent-soft)}
.mfc-friend:focus-visible{outline:2px solid var(--mf-accent);outline-offset:-2px}
.mfc-rowmain{flex:1;min-width:0;display:flex;flex-direction:column;gap:3px}
.mfc-rowtop,.mfc-rowbottom{display:flex;align-items:center;justify-content:space-between;gap:8px;min-width:0}
.mfc-rowname{font-size:15.5px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mfc-rowtime{flex:none;font-size:11.5px;color:var(--mf-faint)}
.mfc-rowtime.is-unread{color:var(--mf-accent);font-weight:800}
.mfc-preview{flex:1;min-width:0;font-size:13.5px;color:var(--mf-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mfc-preview.is-unread{color:var(--mf-text);font-weight:700}
.mfc-preview.is-muted{font-style:italic;color:var(--mf-faint)}
.mfc-badge{flex:none;min-width:22px;height:22px;padding:0 7px;border-radius:999px;display:grid;place-items:center;background:var(--mf-grad);color:#fff;
  font-size:11.5px;font-weight:800}

.mfc-state{margin:30px 18px;text-align:center;color:var(--mf-muted);font-size:14px;line-height:1.5}
.mfc-state .mfc-empty-ico{width:64px;height:64px;margin:0 auto 12px;border-radius:22px;display:grid;place-items:center;background:var(--mf-accent-soft);color:var(--mf-accent);font-size:30px}
.mfc-state h3{margin:0 0 4px;color:var(--mf-text);font-size:17px}
.mfc-state button{margin-top:12px;display:inline-flex;align-items:center;gap:6px;height:36px;padding:0 14px;border-radius:12px;border:1px solid var(--mf-border-strong);
  background:var(--mf-surface-2);color:var(--mf-text);font:inherit;font-size:13.5px;font-weight:700;cursor:pointer}
.mfc-state button svg{font-size:18px}

.mfc-skrow{display:flex;align-items:center;gap:12px;padding:10px 18px}
.mfc-skrow .c{width:50px;height:50px;border-radius:50%;flex:none}
.mfc-skrow .l{flex:1;display:grid;gap:8px}
.mfc-skrow .c,.mfc-skrow .l i{background:linear-gradient(90deg,var(--mf-surface-2) 25%,var(--mf-surface-3) 37%,var(--mf-surface-2) 63%);background-size:400% 100%;
  animation:mfc-shimmer 1.4s ease infinite}
.mfc-skrow .l i{display:block;height:11px;border-radius:6px}

@media (prefers-reduced-motion:reduce){
  .mfc-skrow .c,.mfc-skrow .l i{animation:none!important}
}
`;
injectStyles('mfc-inbox-css', INBOX_CSS);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const nameOf = (u) => (u && ((u.name && u.name.trim()) || u.username)) || 'Friend';

function formatTime(ts, now) {
  if (!ts) return '';
  const diff = Math.max(0, now - ts);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function sortRows(rows) {
  return [...rows].sort(
    (a, b) => b.lastTs - a.lastTs || nameOf(a.user).localeCompare(nameOf(b.user))
  );
}

function subscribeSafe(collection, handler) {
  try {
    const p = pb.collection(collection).subscribe('*', handler);
    if (p && p.catch) p.catch(() => {});
    return p;
  } catch {
    return null;
  }
}

function unsubSafe(u) {
  try {
    if (u && u.then) u.then((f) => f && f()).catch(() => {});
    else if (u) u();
  } catch {}
}

/* ------------------------------------------------------------------ */
/* Inbox                                                               */
/* ------------------------------------------------------------------ */
export default function ChatInbox({ onSelect, selectedFriendId, onClose }) {
  const { user } = useAuth();
  const me = user && user.id;

  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [now, setNow] = useState(Date.now());

  const friendsRef = useRef([]);
  const selectedRef = useRef(selectedFriendId || null);
  const loadSeq = useRef(0);
  const reloadTimer = useRef(null);
  const lastWake = useRef(0);
  const freshTried = useRef(new Set());

  friendsRef.current = friends;
  selectedRef.current = selectedFriendId || null;

  // relative times stay fresh
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  /* ---------- preview text ---------- */
  const decryptPreview = useCallback(async (friendId, rec) => {
    try {
      let ring = await resolveRing(me, friendId, false).catch(() => null);
      if (ring) {
        try { return await decryptWithRing(ring, rec.ciphertext, rec.iv); } catch {}
      }
      // the other person may have just created the secure session: look once more
      if (freshTried.current.has(rec.id)) return null;
      freshTried.current.add(rec.id);
      ring = await resolveRing(me, friendId, false, true).catch(() => null);
      if (!ring) return null;
      return await decryptWithRing(ring, rec.ciphertext, rec.iv);
    } catch {
      return null;
    }
  }, [me]);

  const markDelivered = useCallback((recId) => {
    pb.collection('messages')
      .update(recId, { status: 'delivered', deliveredAt: new Date().toISOString() }, NC)
      .catch(() => {});
  }, []);

  /* ---------- full load (first open, new match, coming back online) ---------- */
  const loadFriends = useCallback(async (silent = false) => {
    if (!me) return;
    const seq = ++loadSeq.current;
    if (!silent) { setLoading(true); setError(''); }

    try {
      const matches = await pb.collection('match_sessions').getFullList({
        filter: `unlocked=true && (owner="${me}" || target="${me}")`,
        ...NC,
      });

      const ids = Array.from(new Set(
        matches.map((m) => (m.owner === me ? m.target : m.owner)).filter(Boolean)
      ));

      const [users, recent, unreadList] = await Promise.all([
        Promise.all(ids.map((id) => pb.collection('users').getOne(id, NC).catch(() => null))),
        pb.collection('messages')
          .getList(1, 200, { filter: `from="${me}" || to="${me}"`, sort: '-created', ...NC })
          .then((r) => r.items)
          .catch(() => []),
        pb.collection('messages')
          .getFullList({ filter: `to="${me}" && status!="read"`, fields: 'id,from,status', ...NC })
          .catch(() => []),
      ]);

      // latest message per friend (one query for everybody)
      const latest = new Map();
      for (const rec of recent) {
        const fid = rec.from === me ? rec.to : rec.from;
        if (!latest.has(fid)) latest.set(fid, rec);
      }

      // older conversations that did not make the recent list
      const missing = users.filter((u) => u && !latest.has(u.id));
      await Promise.all(missing.map(async (u) => {
        try {
          const r = await pb.collection('messages').getList(1, 1, {
            filter: `(from="${me}" && to="${u.id}") || (from="${u.id}" && to="${me}")`,
            sort: '-created',
            ...NC,
          });
          if (r.items[0]) latest.set(u.id, r.items[0]);
        } catch {}
      }));

      const unread = new Map();
      for (const r of unreadList) unread.set(r.from, (unread.get(r.from) || 0) + 1);

      // messages that arrived while we were away are now "delivered"
      unreadList
        .filter((r) => r.status === 'sent' && r.from !== selectedRef.current)
        .forEach((r) => markDelivered(r.id));

      const rows = await Promise.all(
        users.filter(Boolean).map(async (u) => {
          const msg = latest.get(u.id);
          return {
            id: u.id,
            user: u,
            hasMsg: !!msg,
            lastText: msg ? await decryptPreview(u.id, msg) : null,
            lastFrom: msg ? msg.from : '',
            lastTs: msg ? toTime(msg.created) : 0,
            lastMessageId: msg ? (msg.messageId || msg.id) : '',
            unread: u.id === selectedRef.current ? 0 : (unread.get(u.id) || 0),
          };
        })
      );

      if (seq !== loadSeq.current) return;   // a newer load already finished
      setFriends(sortRows(rows));
      setError('');
    } catch (err) {
      console.error('Inbox load failed', err);
      if (seq === loadSeq.current && !silent) setError('Could not load your chats.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [me, decryptPreview, markDelivered]);

  const scheduleReload = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => loadFriends(true), 500);
  }, [loadFriends]);

  const refreshUnread = useCallback(async () => {
    try {
      const list = await pb.collection('messages').getFullList({
        filter: `to="${me}" && status!="read"`,
        fields: 'id,from,status',
        ...NC,
      });
      const counts = new Map();
      for (const r of list) counts.set(r.from, (counts.get(r.from) || 0) + 1);
      setFriends((prev) => prev.map((f) => ({
        ...f,
        unread: f.id === selectedRef.current ? 0 : (counts.get(f.id) || 0),
      })));
    } catch {}
  }, [me]);

  /* ---------- first load ---------- */
  useEffect(() => {
    if (!me) return undefined;
    loadFriends(false);
    return () => clearTimeout(reloadTimer.current);
  }, [me, loadFriends]);

  /* ---------- opening a chat clears its badge right away ---------- */
  useEffect(() => {
    if (!selectedFriendId) return;
    setFriends((prev) => prev.map((f) => (f.id === selectedFriendId && f.unread ? { ...f, unread: 0 } : f)));
  }, [selectedFriendId]);

  /* ---------- realtime ---------- */
  useEffect(() => {
    if (!me) return undefined;

    // messages: update just the chat that changed
    const subMsg = subscribeSafe('messages', (e) => {
      const rec = e && e.record;
      if (!rec) return;
      const fromMe = rec.from === me;
      const toMe = rec.to === me;
      if (!fromMe && !toMe) return;

      const fid = fromMe ? rec.to : rec.from;
      if (!friendsRef.current.some((f) => f.id === fid)) { scheduleReload(); return; }

      if (e.action === 'create') {
        const open = fid === selectedRef.current;
        if (toMe && !open) {
          playSound('receive');
          if (rec.status === 'sent') markDelivered(rec.id);   // an open chat marks it read itself
        }
        decryptPreview(fid, rec).then((text) => {
          setFriends((prev) => sortRows(prev.map((f) => (
            f.id !== fid ? f : {
              ...f,
              hasMsg: true,
              lastText: text,
              lastFrom: rec.from,
              lastTs: toTime(rec.created),
              lastMessageId: rec.messageId || rec.id,
              unread: toMe && !open && rec.status !== 'read' ? f.unread + 1 : f.unread,
            }
          ))));
        });
      } else if (e.action === 'update') {
        if (toMe) {
          pb.collection('messages')
            .getList(1, 1, { filter: `to="${me}" && from="${fid}" && status!="read"`, ...NC })
            .then((r) => setFriends((prev) => prev.map((f) => (
              f.id === fid ? { ...f, unread: fid === selectedRef.current ? 0 : r.totalItems } : f
            ))))
            .catch(() => {});
        }
      } else if (e.action === 'delete') {
        scheduleReload();
      }
    });

    // users: presence / profile changes (no reload, just patch the row)
    const subUsers = subscribeSafe('users', (e) => {
      const rec = e && e.record;
      if (!rec || e.action !== 'update') return;
      if (!friendsRef.current.some((f) => f.id === rec.id)) return;
      setFriends((prev) => prev.map((f) => (f.id === rec.id ? { ...f, user: { ...f.user, ...rec } } : f)));
    });

    // new / unlocked matches
    const subMatch = subscribeSafe('match_sessions', (e) => {
      const rec = e && e.record;
      if (!rec) return;
      if (rec.owner === me || rec.target === me) scheduleReload();
    });

    const onRead = () => refreshUnread();
    window.addEventListener('chat-read', onRead);

    const wake = () => {
      const t = Date.now();
      if (t - lastWake.current < 5000) return;
      lastWake.current = t;
      loadFriends(true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') wake(); };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubSafe(subMsg);
      unsubSafe(subUsers);
      unsubSafe(subMatch);
      window.removeEventListener('chat-read', onRead);
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [me, decryptPreview, markDelivered, scheduleReload, refreshUnread, loadFriends]);

  /* ---------- derived ---------- */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) =>
      nameOf(f.user).toLowerCase().includes(q) || String(f.user.username || '').toLowerCase().includes(q)
    );
  }, [friends, query]);

  const totalUnread = useMemo(() => friends.reduce((n, f) => n + (f.unread || 0), 0), [friends]);

  const handleSelect = (f) => {
    setFriends((prev) => prev.map((x) => (x.id === f.id ? { ...x, unread: 0 } : x)));
    if (onSelect) onSelect({ ...f.user });
  };

  /* ---------- UI ---------- */
  return (
    <div className="mfc-inbox">
      <div className="mfc-side-head">
        <h2 className="mfc-side-title">
          Messages
          {totalUnread > 0 && <span className="mfc-total">{totalUnread > 99 ? '99+' : totalUnread}</span>}
        </h2>
        {onClose && (
          <button type="button" className="mfc-iconbtn" onClick={onClose} aria-label="Close chats">
            <CloseRoundedIcon />
          </button>
        )}
      </div>

      <div className="mfc-search">
        <SearchRoundedIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chats"
          aria-label="Search chats"
          autoComplete="off"
        />
      </div>

      {loading && (
        <div aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="mfc-skrow" key={i}>
              <div className="c" />
              <div className="l"><i style={{ width: '42%' }} /><i style={{ width: '70%' }} /></div>
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="mfc-state">
          <div className="mfc-empty-ico">⚠️</div>
          <h3>Couldn't load chats</h3>
          <div>{error}</div>
          <button type="button" onClick={() => loadFriends(false)}><RefreshRoundedIcon /> Try again</button>
        </div>
      )}

      {!loading && !error && friends.length === 0 && (
        <div className="mfc-state">
          <div className="mfc-empty-ico"><ChatBubbleOutlineRoundedIcon fontSize="inherit" /></div>
          <h3>No chats yet</h3>
          <div>Finish the matching tasks with a friend and your chat will appear here.</div>
        </div>
      )}

      {!loading && !error && friends.length > 0 && visible.length === 0 && (
        <div className="mfc-state">No chats match “{query}”.</div>
      )}

      {!loading && !error && visible.length > 0 && (
        <ul className="mfc-in-list">
          {visible.map((f) => {
            const u = f.user;
            const active = selectedFriendId === f.id;
            const unread = f.unread || 0;

            let preview;
            let muted = false;
            if (!f.hasMsg) { preview = 'Say hi 👋'; muted = true; }
            else if (f.lastText == null) { preview = '🔒 Encrypted message'; muted = true; }
            else preview = `${f.lastFrom === me ? 'You: ' : ''}${f.lastText}`;

            return (
              <li key={f.id}>
                <button
                  type="button"
                  className={`mfc-friend${active ? ' is-active' : ''}`}
                  onClick={() => handleSelect(f)}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="mfc-avwrap">
                    <Avatar user={u} size={50} />
                    <i className={`mfc-dot${isUserOnline(u) ? ' on' : ''}`} />
                  </span>

                  <span className="mfc-rowmain">
                    <span className="mfc-rowtop">
                      <b className="mfc-rowname">{nameOf(u)}</b>
                      <time className={`mfc-rowtime${unread ? ' is-unread' : ''}`}>{formatTime(f.lastTs, now)}</time>
                    </span>
                    <span className="mfc-rowbottom">
                      <span className={`mfc-preview${unread ? ' is-unread' : ''}${muted ? ' is-muted' : ''}`}>{preview}</span>
                      {unread > 0 && <span className="mfc-badge">{unread > 99 ? '99+' : unread}</span>}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}